import { useEffect, useRef, useState } from 'react';
import LiveChoiceButtons from './LiveChoiceButtons';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import LiveToolbarIcon from './LiveToolbarIcon';
import { parseLiveClosingTime } from '../../../User/Live/LiveClosingCountdown';
import LiveDetailDialog from './LiveDetailDialog';

type Info = { live_finish: string; live_limit: number; live_status: string };
const formatTime = (time: number) => new Date(time).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });

export default function LiveTimeExtension({ liveSeq, disabled, onExtended }: { liveSeq: number; disabled: boolean; onExtended: () => void }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<Info | null>(null);
  const [hours, setHours] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const request = useRef(0);
  const [error, setError] = useState('');
  const load = async () => {
    const id = ++request.current;
    setLoading(true); setInfo(null);
    try {
      const { data } = await api.get(url.Communication_url.get_live_info, { params: { seq: liveSeq } });
      if (id !== request.current) return;
      if (!parseLiveClosingTime(data.live_finish) || !Number.isSafeInteger(Number(data.live_limit)) || Number(data.live_limit) <= 0) throw new Error();
      setInfo(data);
    } catch { if (id === request.current) setError('방송 정보를 불러오지 못했습니다. 다시 불러와주세요.'); }
    finally { if (id === request.current) setLoading(false); }
  };
  useEffect(() => {
    if (!open) return;
    setError(''); setHours(1); void load();
    return () => { request.current += 1; };
  }, [open, liveSeq]);
  const finish = parseLiveClosingTime(info?.live_finish);
  const coins = Number(info?.live_limit || 0) * hours * 114 / 25;
  const canExtend = finish !== null && finish > Date.now() && ['예약', '리허설', '방송중'].includes(info?.live_status || '');
  const extend = async () => {
    if (busy.current || !canExtend || finish === null) return;
    busy.current = true; setSaving(true); setError('');
    try {
      await api.patch(`${SERVER_ADDRESS}live/extend-time`, {
        seq: liveSeq, hours, expected_finish: new Date(finish).toISOString(), expected_coins: coins,
      });
      setOpen(false);
      onExtended();
    } catch (failure: any) {
      await load();
      const detail = failure.response?.data?.message;
      setError(typeof detail === 'string' ? detail : '시간을 연장하지 못했습니다. 방송 정보와 코인 잔액을 확인해주세요.');
    } finally { busy.current = false; setSaving(false); }
  };
  return <>
    <AdminActionButton $accented type="button" disabled={disabled} aria-haspopup="dialog" onClick={() => setOpen(true)}><LiveToolbarIcon name="clock" />시간 연장</AdminActionButton>
    <LiveDetailDialog open={open} title="방송 시간 연장" size="compact" onClose={() => { if (!busy.current) setOpen(false); }} closeOnlyByButton={saving}>
      <Content>
        {loading && <p role="status">방송 정보를 불러오는 중입니다.</p>}
        {info && <>
          <dl><div><dt>현재 종료 시간</dt><dd>{finish === null ? '확인 필요' : formatTime(finish)}</dd></div><div><dt>수용 인원</dt><dd>{Number(info.live_limit).toLocaleString()}명</dd></div></dl>
          <LiveChoiceButtons label="연장 시간" value={String(hours)} disabled={saving || loading} onChange={value => setHours(Number(value))}
            options={[{ value: '1', label: '1시간' }, { value: '2', label: '2시간' }]} />
          <dl><div><dt>연장 후 종료</dt><dd>{finish === null ? '확인 필요' : formatTime(finish + hours * 3_600_000)}</dd></div><div><dt>사용 코인</dt><dd><strong>{coins.toLocaleString()}C</strong></dd></div></dl>
          {!canExtend && <p>종료 전인 예약·리허설·방송 중 라이브만 연장할 수 있습니다.</p>}
        </>}
        {error && <p role="alert">{error}</p>}
        <Actions><AdminActionButton type="button" disabled={saving || loading} onClick={() => { setError(''); void load(); }}>다시 불러오기</AdminActionButton><AdminActionButton type="button" $primary disabled={!canExtend || saving || loading} onClick={() => void extend()}>{saving ? '연장 중…' : `${coins.toLocaleString()}C 사용 · 연장`}</AdminActionButton></Actions>
      </Content>
    </LiveDetailDialog>
  </>;
}

const Content = styled.div`
  display: flex; flex-direction: column; gap: var(--space-4); font-size: var(--text-body);
  dl, p { margin: 0; } dl { display: grid; gap: var(--space-3); }
  dl > div { display: flex; justify-content: space-between; gap: var(--space-3); }
  dt { color: var(--color-text-secondary); } dd { margin: 0; text-align: right; } strong { color: var(--color-text-link); }
  label { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); }
  select { min-width: 120px; height: var(--control-lg); padding: 0 var(--space-3); font: inherit; border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); }
  select:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  [role="alert"] { color: var(--color-text-danger); }
`;
const Actions = styled.div`display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: var(--space-2);`;
